// src/components/Notes.jsx
import React, { useState, useEffect } from 'react';
import api from '../api/api';
import moment from 'moment-timezone';
import 'moment/locale/es';
import '../styles/Notes.css';
import { useNavigate } from 'react-router-dom';

const Notes = ({ user }) => {
  const navigate = useNavigate();

  const [notes, setNotes] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);

  // Variables para el formulario de nueva nota o edición
  const [newImportance, setNewImportance] = useState('');
  const [newContent, setNewContent] = useState('');
  const [newAuthor, setNewAuthor] = useState('');

  const handleUnauthorized = () => {
    localStorage.removeItem('token');
    navigate('/login'); // cambia a '/login' si tu login está en otra ruta
  };

  useEffect(() => {
    fetchNotes();
    // Polling: refresca las notas cada 15s
    const intervalId = setInterval(fetchNotes, 15000);
    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Obtiene la lista de notas desde el backend
  const fetchNotes = async () => {
    try {
      const res = await api.get('/api/notes');
      setNotes(res.data);
    } catch (error) {
      console.error('Error al obtener notas:', error);
      if (error?.response?.status === 401) return handleUnauthorized();
    }
  };

  // Filtra las notas según el término de búsqueda
  const filteredNotes = notes.filter(
    (note) =>
      (note.content || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (note.importance || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Abre el modal para crear una nueva nota
  const handleAddNote = () => {
    setNewImportance('');
    setNewContent('');
    setNewAuthor(user?.nombre || '');
    setSelectedNote(null);
    setShowModal(true);
  };

  // Envía la nueva nota al backend
  const handleCreateNote = async () => {
    try {
      const payload = {
        importance: newImportance,
        content: newContent,
        createdBy: newAuthor || 'desconocido',
      };
      await api.post('/api/notes', payload);
      setShowModal(false);
      fetchNotes();
    } catch (error) {
      console.error('Error al crear la nota:', error);
      if (error?.response?.status === 401) return handleUnauthorized();
    }
  };

  // Manejo para borrar una nota
  const handleDeleteNote = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar esta nota?')) return;
    try {
      await api.delete(`/api/notes/${id}`);
      fetchNotes();
    } catch (error) {
      console.error('Error al eliminar nota:', error);
      if (error?.response?.status === 401) return handleUnauthorized();
    }
  };

  // Abre el modal para ver o editar una nota
  const handleViewDetail = (note) => {
    setSelectedNote(note);
    setShowModal(true);
    setNewImportance(note.importance);
    setNewContent(note.content);
    setNewAuthor(note.createdBy);
  };

  // Actualiza la nota existente
  const handleUpdateNote = async () => {
    try {
      const payload = {
        importance: newImportance,
        content: newContent,
        createdBy: newAuthor || 'desconocido',
      };
      await api.put(`/api/notes/${selectedNote.id}`, payload);
      setShowModal(false);
      fetchNotes();
    } catch (error) {
      console.error('Error al actualizar nota:', error);
      if (error?.response?.status === 401) return handleUnauthorized();
    }
  };

  return (
    <div className="notes-container">
      <header className="notes-header">
        <h1>Notas</h1>
        <div className="notes-search-bar">
          <input
            type="text"
            className="notes-search-input"
            placeholder="Buscar nota o importancia..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <button className="notes-search-button">Buscar</button>
        </div>
        <button className="notes-add-button" onClick={handleAddNote}>
          Añadir Nota
        </button>
      </header>

      {/* Vista de Escritorio */}
      <div className="only-desktop">
        <table className="notes-table">
          <thead>
            <tr>
              <th>Importancia</th>
              <th>Nota</th>
              <th>Creado Por</th>
              <th>Fecha</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {filteredNotes.map((note) => (
              <tr key={note.id}>
                <td>{note.importance}</td>
                <td>{note.content}</td>
                <td>{note.createdBy}</td>
                <td>
                  {moment(note.createdAt)
                    .tz('America/Costa_Rica')
                    .format('DD/MM/YYYY h:mma')}
                </td>
                <td>
                  <button onClick={() => handleViewDetail(note)}>Ver/Editar</button>
                  <button onClick={() => handleDeleteNote(note.id)}>Borrar</button>
                </td>
              </tr>
            ))}
            {filteredNotes.length === 0 && (
              <tr>
                <td colSpan="5">No se encontraron notas.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Vista Móvil */}
      <div className="only-mobile">
        {filteredNotes.map((note) => (
          <div key={note.id} className="note-card">
            <p>
              <strong>Importancia:</strong> {note.importance}
            </p>
            <p>
              <strong>Nota:</strong> {note.content}
            </p>
            <p>
              <strong>Autor:</strong> {note.createdBy}
            </p>
            <p>
              <strong>Fecha:</strong>{' '}
              {moment(note.createdAt).tz('America/Costa_Rica').format('DD/MM/YYYY h:mma')}
            </p>
            <div className="note-card-actions">
              <button className="detail-button" onClick={() => handleViewDetail(note)}>
                Ver/Editar
              </button>
              <button className="delete-button" onClick={() => handleDeleteNote(note.id)}>
                Borrar
              </button>
            </div>
          </div>
        ))}
        {filteredNotes.length === 0 && (
          <p style={{ textAlign: 'center', marginTop: '1rem' }}>
            No se encontraron notas.
          </p>
        )}
      </div>

      {/* Modal para crear/editar nota */}
      {showModal && (
        <div className="notes-modal-overlay">
          <div className="notes-modal">
            <h2>{selectedNote ? 'Editar Nota' : 'Nueva Nota'}</h2>

            <div className="form-group">
              <label>Importancia:</label>
              <input
                type="text"
                value={newImportance}
                onChange={(e) => setNewImportance(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Nota:</label>
              <textarea
                rows="4"
                value={newContent}
                onChange={(e) => setNewContent(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Autor:</label>
              <input
                type="text"
                value={newAuthor}
                onChange={(e) => setNewAuthor(e.target.value)}
              />
            </div>

            <div className="notes-modal-actions">
              {selectedNote ? (
                <button onClick={handleUpdateNote}>Actualizar</button>
              ) : (
                <button onClick={handleCreateNote}>Crear Nota</button>
              )}
              <button onClick={() => setShowModal(false)}>Cerrar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Notes;
